import { z } from 'zod';
import {
    CleaningFeeType,
    ExperienceType,
    FeeApplies,
    FeeType,
} from '../../../../enums/experience';
import {
    CleaningFee,
    Experience,
    PropertyDetails,
    TieredCleaningFee,
} from '../../../../types/experience';
import { sum } from '../../../../../shared/utils';
import { SpaceSchema } from '../Amenities/schemas/spaces.schema';

const PricingSchema = z.object({
    chargeType: z.literal('flexiblePrice'),
    nightPrice: z.coerce
        .number()
        .multipleOf(0.01, { message: 'Invalid currency format.' })
        .min(1, { message: 'At least A$1.00 required' }),
    nightCheckin: z.string().min(1, { message: 'Required' }),
    nightCheckout: z.string().min(1, { message: 'Required' }),
    dayPrice: z.coerce
        .number()
        .multipleOf(0.01, { message: 'Invalid currency format.' })
        .min(1, { message: 'At least A$1.00 required' }),
    dayCheckin: z.string().min(1, { message: 'Required ' }),
    dayCheckout: z.string().min(1, { message: 'Required ' }),
    hourPrice: z.coerce
        .number()
        .multipleOf(0.01, { message: 'Invalid currency format.' })
        .min(1, { message: 'At least A$1.00 required' }),
    weekendPrice: z
        .object({
            days: z
                .array(z.enum(['friday', 'saturday', 'sunday']))
                .min(1, { message: 'At least one day required' }),
            dayPrice: z.coerce
                .number()
                .multipleOf(0.01, { message: 'Invalid currency format.' })
                .min(1, { message: 'At least A$1.00 required' }),
            nightPrice: z.coerce
                .number()
                .multipleOf(0.01, { message: 'Invalid currency format.' })
                .min(1, { message: 'At least A$1.00 required' }),
            hourPrice: z.coerce
                .number()
                .multipleOf(0.01, { message: 'Invalid currency format.' })
                .min(1, { message: 'At least A$1.00 required' }),
        })
        .nullable(),
    priceOverrides: z.array(
        z.object({
            date: z.string(),
            nightPrice: z.coerce
                .number()
                .multipleOf(0.01, { message: 'Invalid currency format.' })
                .min(1, { message: 'At least A$1.00 required' }),
            dayPrice: z.coerce
                .number()
                .multipleOf(0.01, { message: 'Invalid currency format.' })
                .min(1, { message: 'At least A$1.00 required' }),
            hourPrice: z.coerce
                .number()
                .multipleOf(0.01, { message: 'Invalid currency format.' })
                .min(1, { message: 'At least A$1.00 required' }),
        }),
    ),
});

// Fixed (basic) pricing schema
export const BasicPricingSchema = z.object({
    chargeType: z.literal('price'),
    unitPrice: z.coerce.number().min(1, { message: 'Required' }),
    type: z.string(), // or use an enum if you want to validate this
    weekendPrice: z
        .object({
            unitPrice: z.coerce.number(),
            days: z.array(z.enum(['friday', 'saturday', 'sunday'])),
        })
        .nullable()
        .optional(),
    priceOverrides: z
        .array(
            z.object({
                date: z.string(),
                unitPrice: z.number(),
            }),
        )
        .default([]),
});

// 1. Base object only
const FlexiblePricingObject = z.object({
    chargeType: z.literal('flexiblePrice'),
    dayPrice: z.coerce.number().min(1, { message: 'Required' }),
    hourPrice: z.coerce.number().min(1, { message: 'Required' }),
    nightPrice: z.coerce.number().min(1, { message: 'Required' }),
    weekendPrice: z
        .object({
            days: z.array(z.enum(['friday', 'saturday', 'sunday'])).optional(),
            nightPrice: z.coerce.number().optional(),
            dayPrice: z.coerce.number().optional(),
            hourPrice: z.coerce.number().optional(),
        })
        .nullable()
        .optional(),
    nightCheckin: z.string().min(1, { message: 'Required' }),
    nightCheckout: z.string().min(1, { message: 'Required' }),
    dayCheckin: z.string().min(1, { message: 'Required' }),
    dayCheckout: z.string().min(1, { message: 'Required' }),
    priceOverrides: z
        .array(
            z.object({
                date: z.string(),
                nightPrice: z.number().optional(),
                dayPrice: z.number().optional(),
                hourPrice: z.number().optional(),
            }),
        )
        .default([]),
});

// 2. Schema with .superRefine applied after
export const FlexiblePricingSchema = FlexiblePricingObject.superRefine(
    (val, ctx) => {
        if (
            val.dayPrice == null &&
            val.nightPrice == null &&
            val.hourPrice == null
        ) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message:
                    'At least one price (day, night, or hour) is required.',
                path: ['dayPrice'],
            });
        }
    },
);

export const TierSchema = z.object({
    rooms: z.coerce.number().min(1, { message: 'Required' }),
    price: z.coerce.number().min(1, { message: 'Required' }),
});
export type Tier = z.infer<typeof TierSchema>;

const FeeAppliesSchema = z.nativeEnum(FeeApplies);

const CleaningFeeSchemaBase = z.object({
    chargeType: z.literal('cleaningFee'),
    cleaningFeeType: z.enum([
        CleaningFeeType.Flat,
        CleaningFeeType.PerRoom,
        CleaningFeeType.Tiered,
        CleaningFeeType.ByZone,
    ]),
    price: z.coerce.number().min(1, { message: 'Required' }).optional(),
    basePrice: z.coerce.number().min(1, { message: 'Required' }).optional(),
    unitPrice: z.coerce.number().min(1, { message: 'Required' }).optional(),
    applies: FeeAppliesSchema.optional(),
    tiers: z.array(TierSchema).optional(),
    spaces: z
        .array(
            SpaceSchema.and(
                z.object({
                    id: z.number().optional(),
                    name: z.string(),
                    cleaningFee: z.coerce.number(),
                }),
            ),
        )
        .optional(),
    guests: z.number().default(1),
    spaceType: z
        .enum(['desk', 'privateOffice', 'meetingRoom', 'entireVenue'])
        .optional(),
});

export const CleaningFeeSchema = CleaningFeeSchemaBase.superRefine(
    (val, ctx) => {
        switch (val.cleaningFeeType) {
            case CleaningFeeType.Flat:
                if (val.price == null) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: 'Required',
                        path: ['price'],
                    });
                }
                break;
            case CleaningFeeType.PerRoom:
                if (val.basePrice == null) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: 'Required',
                        path: ['basePrice'],
                    });
                }
                if (val.unitPrice == null) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: 'Required',
                        path: ['unitPrice'],
                    });
                }
                if (val.applies == null) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: 'Required',
                        path: ['applies'],
                    });
                }
                break;
            case CleaningFeeType.Tiered:
                if (val.basePrice == null) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: 'Required',
                        path: ['basePrice'],
                    });
                }
                if (!val.tiers || val.tiers.length <= 1) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: 'Add at least two tiers',
                        path: ['tiers'],
                    });
                }
                if (val.applies == null) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: 'Required',
                        path: ['applies'],
                    });
                }
                break;
            case CleaningFeeType.ByZone:
                if (val.basePrice == null) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: 'Required',
                        path: ['basePrice'],
                    });
                }
                if (val.applies == null) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: 'Required',
                        path: ['applies'],
                    });
                }
                break;
        }
    },
);

// 3. Final union schema
export const ModulePricingSchema = z.discriminatedUnion('chargeType', [
    BasicPricingSchema,
    FlexiblePricingObject, // not FlexiblePricingSchema (it's a ZodEffects!)
    CleaningFeeSchemaBase,
]);

export type Pricing = z.infer<typeof ModulePricingSchema>;

export const ExperienceCharge = z
    .object({
        feeId: z.string().or(z.number()).optional(),
        type: z.enum(
            [
                'fixed',
                'perHour',
                'perDay',
                'perNight',
                'perHourPerGuest',
                'perDayPerGuest',
                'perNightPerGuest',
                'perDeskPerDay',
                'perDeskPerHalfDay',
                'perOfficePerDay',
                'perOfficePerHalfDay',
                'perMeetingRoomPerDay',
                'perMeetingRoomPerHalfDay',
                'entireVenuePerDay',
                'entireVenuePerHalfDay',
                'entireVenuePerNight',
            ],
            { message: 'Required' },
        ),
        unitPrice: z.coerce
            .number()
            .min(1, { message: 'At least A$1.00 required' })
            .multipleOf(0.01, { message: 'Invalid currency format' }),
        chargeType: z.enum(['fee', 'price']),
        weekendPrice: z
            .object({
                days: z
                    .array(z.enum(['friday', 'saturday', 'sunday']))
                    .min(1, { message: 'Required' }),
                unitPrice: z.coerce
                    .number()
                    .multipleOf(0.01, { message: 'Invalid currency format.' })
                    .min(1, { message: 'At least A$1.00 required' }),
            })
            .optional()
            .nullable(),
        priceOverrides: z
            .array(
                z.object({
                    date: z.string(),
                    unitPrice: z.number(),
                }),
            )
            .optional()
            .default([]),
        // TODO we don't need this in schema
        name: z.string().optional(),
    })
    .superRefine((data, ctx) => {
        if (data.chargeType === 'fee' && !data.feeId) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'Required',
                path: ['feeId'],
            });
        }
    });

export const BasePriceSchema = z.object({
    prices: z.array(ExperienceCharge),
});

export const FeesSchema = z.object({
    fees: z.array(ExperienceCharge),
});

export const ExperiencePricingSchema = z.object({
    charges: z.array(ExperienceCharge.or(PricingSchema).or(CleaningFeeSchema)),
    /** Additional fields for validation */
    minHireHours: z.number().optional(),
    halfDayCheckinTime: z.string().optional().nullable(),
    halfDayCheckoutTime: z.string().optional().nullable(),
    details: z
        .object({
            desks: z.number().optional(),
            privateOffices: z.number().optional(),
            meetingRooms: z.number().optional(),
            bedrooms: z.number().optional(),
        })
        .optional(),
    linkedModules: z.array(z.object({ id: z.number() })).optional(),
    linkType: z.enum(['block', 'combine']).optional(),
    // must be sent alongside details for backend validation
    spaces: z.array(SpaceSchema.and(z.object({ id: z.number().optional() }))),
});

export const getExperiencePricingSchema = (
    experience: Experience,
    allAssets: Experience[] = [],
) => {
    if (experience.type === ExperienceType.Commercial) {
        return ExperiencePricingSchema.superRefine((val, ctx) => {
            if (val.minHireHours && val.minHireHours < 24) {
                const requiredHalfDayPriceTypes = [
                    FeeType.EntireVenuePerHalfDay,
                ];
                if (
                    val.details?.privateOffices &&
                    val.details?.privateOffices > 0
                ) {
                    requiredHalfDayPriceTypes.push(FeeType.PerOfficePerHalfDay);
                }
                if (
                    val.details?.meetingRooms &&
                    val.details?.meetingRooms > 0
                ) {
                    requiredHalfDayPriceTypes.push(
                        FeeType.PerMeetingRoomPerHalfDay,
                    );
                }

                const hasMissingPrice = requiredHalfDayPriceTypes.some(
                    (priceType) =>
                        !val.charges
                            .map((charge: any) => charge?.type)
                            .includes(priceType),
                );

                if (hasMissingPrice) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: `Configure half day prices or set minimum hire period to 1 day or more. Click on "Base Price" to configure half day prices.`,
                        path: ['charges'],
                    });
                }
            }
        });
    }

    if (experience.type === ExperienceType.Property) {
        return ExperiencePricingSchema.superRefine((val, ctx) => {
            const cleaningFee = val.charges.find(
                (charge) => charge.chargeType === 'cleaningFee',
            );
            if (
                !cleaningFee ||
                !(
                    (cleaningFee as CleaningFee).cleaningFeeType ===
                    CleaningFeeType.Tiered
                )
            ) {
                return;
            }

            const linkedModules =
                allAssets?.filter((asset: any) =>
                    experience.linkedModules.some(({ id }) => id === asset.id),
                ) || [];

            const linkedModulesBedrooms = sum(
                linkedModules.map(
                    (module: Experience) =>
                        (module.details as PropertyDetails).bedrooms,
                ),
            );
            const experienceRooms =
                (val.details?.bedrooms ?? 0) +
                (val.linkType === 'combine' ? linkedModulesBedrooms : 0);
            const tierRooms = (cleaningFee as TieredCleaningFee).tiers.map(
                (tier: Tier) => tier.rooms,
            );
            const maxRoomsSet = Math.max(...tierRooms);

            if (maxRoomsSet < experienceRooms) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: `Tiers should cover all ${experienceRooms} rooms ${
                        linkedModulesBedrooms > 0 &&
                        experience.linkType === 'combine'
                            ? 'across the linked properties'
                            : ''
                    }`,
                    path: ['charges'],
                });
            }

            if (
                val.linkType === 'combine' &&
                val.linkedModules &&
                val.linkedModules.length > 0
            ) {
                const hasByZoneCleaningFee = val.charges.find(
                    (charge) =>
                        charge.chargeType === 'cleaningFee' &&
                        charge.cleaningFeeType === CleaningFeeType.ByZone,
                );

                if (hasByZoneCleaningFee) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message:
                            'Experiences with by zone cleaning fees cannot be combined with other experiences.',
                        path: ['charges'],
                    });
                }
            }
        });
    }

    return ExperiencePricingSchema;
};
